/**
 * Geldformatierung passend zur Bestellwaehrung - 1:1-Portierung aus `index.html` (Stand
 * 2026-08-08). Die Rechenwege sind woertlich uebernommen; ergaenzt wurden ausschliesslich Typen und
 * der Umbau der DB-Bindung (siehe unten).
 *
 * ================= Warum ein eigenes Modul =================
 *
 * `fmtMoney`/`fmtMoney0` stehen im Original zwischen `orderCurrency` (5124) und `fmtMixedTotals`
 * (5136) und gehoeren fachlich zu keinem der bestehenden sechs `lib/`-Module:
 * - `lib/format.ts` waere der naheliegende Ort (dort liegen `EUR`, `EUR0`, `NUM`), scheidet aber
 *   aus: es importiert heute NICHTS aus `lib/` und ist damit die Wurzel des Importgraphen. Ein
 *   Import von `orderCurrency` aus `lib/suppliers.ts` (das seinerseits `EUR`/`NUM` aus `format.ts`
 *   holt) waere der Zyklus format -> suppliers -> format.
 * - `lib/suppliers.ts` waere zyklusfrei, ist aber das Modul der Lieferantenlogik; eine
 *   Waehrungs-FORMATIERUNG (Intl-Ausgabe) gehoert dort so wenig hin wie `fmtDate`.
 * Deshalb ein eigenes, blattnahes Modul - dieselbe Ueberlegung, die `lib/types.ts` und
 * `lib/landedCost.ts` bereits von ihren Nachbarn getrennt hat.
 *
 * ================= Der Umbau: DB-Bindung -> Argument =================
 *
 * Im Original lesen beide Funktionen die Bestellwaehrung ueber `orderCurrency(o)` aus dem globalen
 * `DB.suppliers`. Hier bekommen sie die Lieferantenliste als drittes Argument `suppliers` und
 * reichen sie nur an `orderCurrency` weiter - aufgeloeste Datenlisten stehen am Ende, wie es die
 * Regel der Herkunftstabelle vorgibt (siehe
 * `docs/superpowers/reference/2026-08-09-frontend-lib-herkunft.md`).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die USD-Zweige runden VOR dem Formatieren (`Math.round(v * 100) / 100` bzw. `Math.round(v)`)
 *    und geben das Ergebnis durch `NUM` (kein `style: "currency"`), mit dem Waehrungszeichen als
 *    vorangestelltem Literal `'$ '`. Die EUR-Zweige runden NICHT selbst, sondern ueberlassen das
 *    `EUR`/`EUR0`. Beide Seiten sind damit bei halben Cent-Betraegen unterschiedlich gerundet
 *    (`Math.round` rundet die Haelfte AUFWAERTS, auch bei negativen Werten Richtung +unendlich;
 *    `Intl.NumberFormat` rundet die Haelfte vom Nullpunkt WEG). Woertlich uebernommen - der
 *    Differenztest fuehrt genau diese Betraege gegen das Original.
 * 2. `fmtMoney0` benutzt im USD-Zweig `NUM` (bis zu drei Nachkommastellen moeglich, hier aber durch
 *    das vorherige `Math.round` nie erreicht) und im EUR-Zweig `EUR0`
 *    (`maximumFractionDigits: 0`) - zwei verschiedene Wege zum selben Ziel "ganze Betraege".
 */
import { EUR, EUR0, NUM } from "./format.ts";
import { orderTotal } from "./payments.ts";
import { orderCurrency } from "./suppliers.ts";

// Die Typdeklarationen stehen seit Task 1 gebuendelt in `lib/types.ts` (Begruendung dort).
import type { Order, Supplier } from "./types.ts";
export type { Order, Supplier };

/**
 * index.html:5128 - Geldformat passend zur Bestellwaehrung. `DB.suppliers` (ueber `orderCurrency`)
 * ist hier das Argument `suppliers`.
 */
export function fmtMoney(
  o: Order,
  v: number,
  suppliers: readonly Supplier[] | null | undefined,
): string {
  return orderCurrency(o, suppliers) === "USD" ? "$ " + NUM.format(Math.round(v * 100) / 100) : EUR.format(v);
}

/**
 * index.html:5132 - Gerundetes Geldformat (ganze Betraege) fuer Karten & Spaltensummen;
 * Drawer/Stueckpreise nutzen `fmtMoney`. `DB.suppliers` (ueber `orderCurrency`) ist hier das
 * Argument `suppliers`.
 */
export function fmtMoney0(
  o: Order,
  v: number,
  suppliers: readonly Supplier[] | null | undefined,
): string {
  return orderCurrency(o, suppliers) === "USD" ? "$ " + NUM.format(Math.round(v)) : EUR0.format(v);
}

/**
 * index.html:5136 - gemischte Summe einer Bestellliste: der USD-Anteil und der EUR-Anteil stehen
 * NEBENEINANDER (` · `), nicht umgerechnet nebeneinander. Nachportiert in Task 11 (Phase 6) fuer die
 * KPI-Kachel „Unterwegs" der Uebersicht (`index.html:14700`). `DB.suppliers` (ueber `orderCurrency`)
 * ist hier das Argument `suppliers`.
 *
 * Woertlich uebernommene Eigenheiten:
 * 1. Die Aufteilung ist NICHT vollstaendig: `orderCurrency` liefert genau `'USD'` oder `'EUR'`
 *    (`suppliers.ts:222`), beide Zweige sind also abgedeckt - aber eine Bestellung mit Warenwert 0
 *    faellt aus BEIDEN Summen heraus, weil die Schwelle `> 0` auf der SUMME steht. Eine Liste aus
 *    lauter Nullbetraegen liefert deshalb die leere Zeichenkette, nicht `"$ 0"`.
 * 2. Der USD-Zweig rundet VOR dem Formatieren (`Math.round`) und benutzt `NUM` mit vorangestelltem
 *    Literal `'$ '`; der EUR-Zweig ueberlaesst das Runden `EUR0`. Dieselbe Asymmetrie wie in
 *    `fmtMoney0` (siehe Eigenheit 1 im Kopfkommentar).
 * 3. Die Liste wird ZWEIMAL vollstaendig durchlaufen und beide Male neu summiert - keine gemeinsame
 *    Zwischensumme. Die Gleitkommareihenfolge bleibt damit dieselbe wie im Original.
 */
export function fmtMixedTotals(
  orders: readonly Order[],
  suppliers: readonly Supplier[] | null | undefined,
): string {
  const usd = orders.filter((o) => orderCurrency(o, suppliers) === "USD").reduce((s, o) => s + orderTotal(o), 0);
  const eur = orders.filter((o) => orderCurrency(o, suppliers) === "EUR").reduce((s, o) => s + orderTotal(o), 0);
  return [usd > 0 ? "$ " + NUM.format(Math.round(usd)) : "", eur > 0 ? EUR0.format(eur) : ""]
    .filter(Boolean).join(" · ");
}
